<template>
  <div class="forgetpass">
    <van-nav-bar title="找回密码" left-arrow @click-left="$router.go(-1)" />
    <div class="container">
      <div class="title">
        <h3>重置登录密码</h3>
        <p>请输入注册 / 已绑定的手机号，通过短信验证码验证后设置新密码</p>
      </div>

      <div class="form">
        <div class="form-item">
          <input class="inp" type="text" inputmode="numeric" v-model.trim="form.mobile" maxlength="11" placeholder="请输入手机号码">
        </div>
        <div class="form-item">
          <input class="inp" type="text" v-model.trim="form.picCode" maxlength="5" placeholder="请输入图形验证码">
          <img v-if="picUrl" :src="picUrl" alt="图形验证码" title="点击刷新" @click="updatePic">
        </div>
        <div class="form-item">
          <input class="inp" type="text" inputmode="numeric" v-model.trim="form.smsCode" maxlength="6" placeholder="请输入短信验证码">
          <button @click="getMsgCode">{{ count > 0 ? count + '秒后重新发送' : '获取验证码' }}</button>
        </div>
        <div class="form-item">
          <input class="inp" type="password" v-model="form.password" maxlength="20" placeholder="请输入新密码（6-20位）">
        </div>
        <div class="form-item">
          <input class="inp" type="password" v-model="form.confirmPassword" maxlength="20" placeholder="请再次输入新密码">
        </div>
      </div>

      <!-- 保存按钮：校验手机号短信验证码后保存（重置）新密码 -->
      <div class="save-btn" :class="{ disabled: submitting }" @click="resetPassword">
        {{ submitting ? '保存中...' : '保 存' }}
      </div>

      <div class="links">
        <span class="link" @click="$router.replace('/login')">想起密码了？去登录</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getPicCodeApi, sendResetCodeApi, resetPasswordApi } from '@/api/login'
import { Toast } from 'vant'

/**
 * 找回密码（重置登录密码）
 * 流程：手机号 → 图形验证码 → 短信验证码 → 新密码 → 保存
 * 说明：短信验证码 5 分钟有效、一次性使用；未配置阿里云短信时后端为演示模式。
 */
export default {
  name: 'ForgetPassword',
  data () {
    return {
      form: {
        mobile: '',
        picCode: '',
        smsCode: '',
        password: '',
        confirmPassword: ''
      },
      // 图形验证码图片（base64）与标识
      picUrl: '',
      picKey: '',
      // 短信倒计时
      timer: null,
      count: 0,
      submitting: false
    }
  },
  created () {
    this.updatePic()
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    startCountdown () {
      this.clearTimer()
      this.count = 60
      this.timer = setInterval(() => {
        this.count--
        if (this.count <= 0) {
          this.clearTimer()
        }
      }, 1000)
    },
    // 刷新图形验证码
    async updatePic () {
      try {
        const { data: { img, key } } = await getPicCodeApi()
        this.picUrl = img
        this.picKey = key
        this.form.picCode = ''
      } catch (e) {
        console.error(e)
      }
    },
    // 获取短信验证码（手机号必须已注册）
    async getMsgCode () {
      if (!/^1[3-9]\d{9}$/.test(this.form.mobile)) {
        Toast('请输入正确的手机号')
        return
      }
      if (this.form.picCode.length < 4) {
        Toast('请输入图形验证码')
        return
      }
      try {
        const { data } = await sendResetCodeApi(this.form.picCode, this.picKey, this.form.mobile)
        // 演示模式（未配置阿里云短信）后端会返回验证码并自动回填，便于本地联调
        const demoCode = data && (data.demo_code || data.code)
        if (demoCode) {
          this.form.smsCode = demoCode
          Toast('演示模式：验证码已自动填入（未真正发送短信，请在 server/sms_config.php 配置阿里云短信）')
        } else {
          Toast('验证码已发送，请注意查收短信')
        }
        this.startCountdown()
      } catch (err) {
        // 具体错误（如「该手机号未注册」「获取验证码频繁」）由请求拦截器提示
        this.updatePic()
      }
    },
    // 保存（重置密码）
    async resetPassword () {
      const f = this.form
      if (!/^1[3-9]\d{9}$/.test(f.mobile)) {
        Toast('请输入正确的手机号')
        return
      }
      if (!f.smsCode) {
        Toast('请输入短信验证码')
        return
      }
      if (f.password.length < 6 || f.password.length > 20) {
        Toast('密码长度为 6-20 位')
        return
      }
      if (f.password !== f.confirmPassword) {
        Toast('两次输入的密码不一致')
        return
      }
      if (this.submitting) return
      this.submitting = true
      try {
        const { message } = await resetPasswordApi(f.mobile, f.smsCode, f.password)
        this.clearTimer()
        Toast.success(message || '密码重置成功，请使用新密码登录')
        setTimeout(() => {
          this.$router.replace('/login')
        }, 1000)
      } catch (err) {
        Toast(typeof err === 'string' ? err : '重置失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.forgetpass {
  min-height: 100vh;
  background-color: #fff;

  /deep/ .van-nav-bar__text,
  /deep/ .van-nav-bar__arrow { color: #333; }
}

.container {
  padding: 30px 29px 49px;

  .title {
    margin-bottom: 20px;

    h3 {
      font-size: 26px;
      font-weight: 400;
    }

    p {
      line-height: 24px;
      font-size: 13px;
      color: #b8b8b8;
      margin-top: 4px;
    }
  }

  .form-item {
    border-bottom: 1px solid #f3f1f2;
    padding: 8px;
    margin-bottom: 14px;
    display: flex;
    align-items: center;

    .inp {
      display: block;
      border: none;
      outline: none;
      height: 32px;
      font-size: 14px;
      flex: 1;
      min-width: 0;
    }

    img {
      width: 94px;
      height: 31px;
    }

    button {
      height: 31px;
      border: none;
      font-size: 13px;
      color: #cea26a;
      background-color: transparent;
      padding-right: 9px;
      white-space: nowrap;
    }
  }

  .save-btn {
    width: 100%;
    height: 42px;
    margin-top: 30px;
    background: linear-gradient(90deg, #ecb53c, #ff9211);
    color: #fff;
    border-radius: 39px;
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);
    letter-spacing: 2px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.disabled {
      opacity: 0.6;
    }
  }

  .links {
    margin-top: 24px;
    text-align: center;

    .link {
      font-size: 14px;
      color: #ff6b35;
    }
  }
}
</style>
